import { PageTable } from '../../../components-common/layout/views/PageTable';
import { layoutWorkspace } from '../../_common/layout.workspace';
import { ConfigSection } from '@components-fabric';
import { createRouteFor } from '@components-common/routes';
import { presetsSection, presetsSectionBase } from '../routes.section';
import { spaceFunctionsPresetsEntity } from './space-functions-presets.entity';
import { PresetCreatePage } from '../../../components-domain/generation-domain/presets/components/PresetCreatePage';
import { presetSpaceFunctionsCols } from '../../../components-domain/generation-domain/presets/components/_defaults';
import { PresetEditPage } from '../../../components-domain/generation-domain/presets/components/PresetEditPage';
import { ModalContentPrests } from '../../../components-domain/generation-domain/presets/components/ModalContentPresets';
import { compDBAdditionalCheck } from '../../../components-domain/bim-domain/defaults';

const basePath = presetsSectionBase;

/**
 * Configuration Object: editScreen
 *
 * This object defines the configuration for the edit screen of preset space functions.
 * It specifies the component, props, and various data sources for fetching and updating data.
 *
 * @property {Array} children - An array of child configurations for the edit screen.
 */
const editScreen = {
  children: [
    {
      Component: PresetEditPage,
      props: {
        title: 'Edit Preset Space Functions',
        itemFieldForTable: 'spaceFunction',
        toolbar: {
          cat: 'edit',
          left: ['save'],
          right: ['back'],
        },
        form: {
          element: {},
          schema: {},
          mode: 'edit',
        },
        getSourceTable: '/buildingms/presets-spaces/spaces-with-data',
        updateSourceForm: '/buildingms/presets/',
        deleteSource: '/buildingms/presets-spaces',
        modalProps: {
          Component: ModalContentPrests,
          title: 'Add more space functions',
          show: true,
          onClose: () => {},
          onHide: () => {},
          actions: [],
          props: {
            selectedIdField: 'spaceFunctionId',
            getSource: `/buildingms/space-functions/get-spaces-by-preset-id/`,
            createSource: `/buildingms/presets/spaces`,
            columns: presetSpaceFunctionsCols,
          },
        },
      },
    },
  ],
};

/**
 * Configuration Object: spaceFunctionsSection
 *
 * This object defines the configuration for the space functions presets section.
 * It includes the section ID, title, path, layout, and an array of screen configurations.
 *
 * @property {string} id - The ID of the section.
 * @property {string} title - The title of the section.
 * @property {string} path - The URL path for the section.
 * @property {React.Component} layout - The layout component for the section.
 * @property {Array} screens - An array of screen configuration objects.
 */
export const spaceFunctionsSection: ConfigSection = {
  id: 'space_functions_presets',
  title: 'Preset Space Functions',
  path: 'space-functions',
  layout: layoutWorkspace,
  screens: [
    {
      id: 'index',
      path: '.',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'index',
      columns: ['name', { id: 'sourceType' }, 'description'],
      source: spaceFunctionsPresetsEntity.endpoints,
      urlParams: {
        type: 'SPACES',
      },
      actions: [
        {
          id: 'edit',
          actionToDispatch: { payload: { path: '' } },
          additionalChecks: compDBAdditionalCheck,
          displayTooltip: true,
          tooltipText: 'Edit preset',
        },
        {
          id: 'copy',
          actionToDispatch: {
            payload: {
              copy: '/buildingms/presets/clonePreset',
            },
          },
          displayTooltip: true,
          tooltipText: 'Duplicate preset',
        } as any,
        {
          id: 'delete',
          actionToDispatch: {
            payload: {
              endpointToDelete: '/buildingms/presets',
            },
          },
          additionalChecks: compDBAdditionalCheck,
          displayTooltip: true,
          tooltipText: 'Delete preset',
        },
      ],
      Component: PageTable,
    } as any,
    {
      id: 'create',
      path: 'new',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'custom',
      children: [
        {
          Component: PresetCreatePage,
          props: {
            title: 'Create Preset Space Functions',
            form: {
              element: {
                name: '',
                description: '',
                type: 'SPACES',
              },
              schema: {},
              mode: 'create',
            },
            columns: presetSpaceFunctionsCols,
            getPresetItemsSource: '/buildingms/space-functions',
            createPresetItemsSource: '/buildingms/presets/spaces',
            navUrl: '/components-db/presets/space-functions',
          },
        },
      ],
    },
    {
      id: 'edit',
      path: ':presetId',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'custom',
      ...editScreen,
    },
  ],
};

/**
 * Function: createRouteFor
 *
 * This function generates routes for the space functions presets section.
 *
 * @param {ConfigSection} presetsSection - The presets section configuration.
 * @param {string} routeKey - The key for the route.
 *
 * @returns {Object} An object containing the generated routes for space functions presets.
 */
const routesForSpaceFunctions = createRouteFor<
  typeof presetsSection,
  'space_functions'
>(presetsSection, 'space_functions');

/**
 * Object: spaceFunctionsPresetsRoutes
 *
 * This object defines the routes for the space functions presets section.
 *
 * @property {Object} index - The route configuration for the index page.
 * @property {Object} create - The route configuration for the create page.
 * @property {Object} details - The route configuration for the details page.
 */
export const spaceFunctionsPresetsRoutes = {
  index: routesForSpaceFunctions('index', { basePath }),
  create: routesForSpaceFunctions('create', { basePath }),
  details: routesForSpaceFunctions('details', { basePath }),
};

/**
 * Type: SpaceFunctionsRoutesIds
 *
 * This type defines the IDs for the space functions presets routes.
 *
 * @typedef {string} SpaceFunctionsRoutesIds - The ID of the space functions presets route.
 */
export type SpaceFunctionsRoutesIds =
  typeof spaceFunctionsPresetsRoutes[keyof typeof spaceFunctionsPresetsRoutes]['id'];
